// Excel'den toplu içe aktarma — stok kartı ve cari için ortak çekmece.
//
// AKIŞ: şablonu indir → doldur → yükle → ÖN İZLEME → onayla.
//
// Ön izleme adımı isteğe bağlı değil: 500 satırlık bir dosyayı görmeden yazmak,
// tek bir yazım hatasını fark edilmez kılardı. Sunucu aynı dosyayı iki kez
// okuyor (kuru koşu + yazma); dosya küçük, karşılığında durum tutmayan bir
// akış elde ediliyor.
//
// Kullanıcı kararları (22.08.2026): kayıtlı kod ATLANIR, üzerine yazılmaz ·
// hatalı satır dosyanın gerisini durdurmaz.

import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from './Drawer'
import { toast } from './Toast'
import { ApiHatasi, dosyaIndir, dosyaYukle } from '../lib/api'
import { tr } from '../lib/trFormat'

export type IceAktarRaporu = {
  yazildi: boolean
  eklenen: number
  /** Mal kabulde var olan kayıtların güncellenen sayısı; diğer içe aktarmalarda yok. */
  guncellenen?: number
  atlanan: number
  hatali: number
  atlananSatirlar: { satir: number; kod: string; sebep: string }[]
  hataliSatirlar: { satir: number; sebep: string }[]
  ornekKodlar: string[]
}

export default function IceAktarDrawer({
  baslik,
  sablonYolu,
  sablonAdi,
  iceAktarYolu,
  tazeleAnahtari,
}: {
  /** "stok kartı" / "cari" — metinlerde geçiyor. */
  baslik: string
  sablonYolu: string
  sablonAdi: string
  iceAktarYolu: string
  /** İçe aktarma bitince geçersiz kılınacak sorgu anahtarları. */
  tazeleAnahtari: string[]
}) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const secici = useRef<HTMLInputElement>(null)

  const [dosya, setDosya] = useState<File | null>(null)
  const [rapor, setRapor] = useState<IceAktarRaporu | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const hataMetni = (h: unknown) =>
    h instanceof ApiHatasi ? h.message : 'İşlem tamamlanamadı'

  const sablonIndir = async () => {
    try {
      await dosyaIndir(sablonYolu, sablonAdi)
      toast(`${sablonAdi} indirildi — doldurup buradan yükleyin`)
    } catch (h) {
      toast(hataMetni(h))
    }
  }

  const yukle = async (onayla: boolean) => {
    if (!dosya) return
    setBekliyor(true)
    try {
      const yol = onayla ? `${iceAktarYolu}?onayla=true` : iceAktarYolu
      const sonuc = await dosyaYukle<IceAktarRaporu>(yol, dosya)
      setRapor(sonuc)

      if (onayla) {
        tazeleAnahtari.forEach((a) => kuyruk.invalidateQueries({ queryKey: [a] }))
        toast(
          `${tr(sonuc.eklenen, 0)} ${baslik} eklendi` +
            (sonuc.guncellenen ? ` · ${tr(sonuc.guncellenen, 0)} güncellendi` : '') +
            (sonuc.atlanan ? ` · ${tr(sonuc.atlanan, 0)} atlandı` : '') +
            (sonuc.hatali ? ` · ${tr(sonuc.hatali, 0)} hatalı satır` : ''),
        )
      }
    } catch (h) {
      toast(hataMetni(h))
    } finally {
      setBekliyor(false)
    }
  }

  // Dosya değişince eski rapor GİTMELİ: yeni dosya için eski sayıları
  // göstermek kullanıcıyı yanlış şeyi onaylamaya götürürdü.
  const dosyaSec = (f: File | null) => {
    setDosya(f)
    setRapor(null)
  }

  const yazildi = rapor?.yazildi === true

  return (
    <>
      <DrawerGovde>
        <div className="rule">
          <b>1 · Şablonu indirin</b>
          <span>
            Başlık satırını değiştirmeyin. 2. satır örnektir, içe aktarılmaz —
            verilerinizi 3. satırdan itibaren girin. İkinci sayfada sütun sütun
            açıklama var.
          </span>
        </div>

        <button
          className="btn btn-sec"
          type="button"
          style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }}
          onClick={() => void sablonIndir()}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
          {sablonAdi}
        </button>

        <div className="rule">
          <b>2 · Doldurduğunuz dosyayı yükleyin</b>
          <span>
            Kayıtlı bir kod <b>atlanır</b>, üzerine yazılmaz. Hatalı satırlar
            dosyanın gerisini durdurmaz — sağlam satırlar yüklenir, hatalılar
            satır numarasıyla listelenir.
          </span>
        </div>

        <input
          ref={secici}
          type="file"
          accept=".xlsx"
          style={{ display: 'none' }}
          onChange={(e) => dosyaSec(e.target.files?.[0] ?? null)}
        />

        <button
          className="btn btn-ghost"
          type="button"
          style={{ width: '100%', justifyContent: 'center' }}
          onClick={() => secici.current?.click()}
        >
          {dosya ? dosya.name : 'Dosya seçin (.xlsx)'}
        </button>

        {rapor && (
          <>
            <div className="sect-t" style={{ marginTop: 14 }}>
              {yazildi ? 'İçe aktarma tamamlandı' : 'Ön izleme — henüz yazılmadı'}
            </div>

            <table className="grid">
              <tbody>
                <tr>
                  <td>{yazildi ? 'Eklendi' : 'Eklenecek'}</td>
                  <td className="num mono">{tr(rapor.eklenen, 0)}</td>
                </tr>
                {rapor.guncellenen !== undefined && rapor.guncellenen > 0 && (
                  <tr>
                    <td>Güncellenen</td>
                    <td className="num mono">{tr(rapor.guncellenen, 0)}</td>
                  </tr>
                )}
                <tr>
                  <td>Atlandı <span className="cell-sub">(kayıtlı ya da yinelenmiş)</span></td>
                  <td className="num mono">{tr(rapor.atlanan, 0)}</td>
                </tr>
                <tr>
                  <td>Hatalı satır</td>
                  <td className="num mono">{tr(rapor.hatali, 0)}</td>
                </tr>
              </tbody>
            </table>

            {rapor.hataliSatirlar.length > 0 && (
              <>
                <div className="sect-t" style={{ marginTop: 12 }}>Hatalı satırlar</div>
                <table className="grid">
                  <thead>
                    <tr>
                      <th style={{ width: 64 }}>Satır</th>
                      <th>Sebep</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rapor.hataliSatirlar.map((h) => (
                      <tr key={`h${h.satir}`}>
                        <td className="mono">{h.satir}</td>
                        <td className="cell-sub">{h.sebep}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rapor.hatali > rapor.hataliSatirlar.length && (
                  <div className="fld-hint">
                    İlk {rapor.hataliSatirlar.length} satır gösteriliyor · toplam{' '}
                    {tr(rapor.hatali, 0)}
                  </div>
                )}
              </>
            )}

            {rapor.atlananSatirlar.length > 0 && (
              <>
                <div className="sect-t" style={{ marginTop: 12 }}>Atlananlar</div>
                <table className="grid">
                  <thead>
                    <tr>
                      <th style={{ width: 64 }}>Satır</th>
                      <th style={{ width: 120 }}>Kod</th>
                      <th>Sebep</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rapor.atlananSatirlar.map((a) => (
                      <tr key={`a${a.satir}`}>
                        <td className="mono">{a.satir}</td>
                        <td><span className="kod">{a.kod}</span></td>
                        <td className="cell-sub">{a.sebep}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-ghost" type="button" onClick={() => kapat()}>
          {yazildi ? 'Kapat' : 'Vazgeç'}
        </button>

        {!yazildi && (
          <button
            className="btn btn-pri"
            type="button"
            style={{ flex: 1, justifyContent: 'center' }}
            disabled={!dosya || bekliyor || (rapor !== null && rapor.eklenen === 0)}
            title={
              !dosya
                ? 'Önce bir .xlsx dosyası seçin'
                : rapor !== null && rapor.eklenen === 0
                  ? 'Eklenecek satır yok'
                  : undefined
            }
            onClick={() => void yukle(rapor !== null)}
          >
            {rapor === null
              ? 'Kontrol et'
              : `${tr(rapor.eklenen, 0)} kaydı içe aktar`}
          </button>
        )}
      </DrawerAltlik>
    </>
  )
}
